/**
 * Форматтеры раздела «Проекты» (US-26 FR-5).
 *
 * Денежные величины приходят из выдачи **в тенге** (US-27 FR-3.1);
 * масштаб — свойство отображения: плашка укрупняет до млрд ₸, реестр,
 * карточка и виджеты считают в млн ₸. Единица подписывается в заголовке
 * плитки, колонки или блока и в ячейках не повторяется (FR-5.1).
 *
 * Разделитель разрядов — пробел, десятичный — запятая (`ru-RU`, FR-5.2).
 * Отрицательные значения выводятся со знаком и не обнуляются (FR-5.5),
 * процент больше 100 % выводится как есть, а полоса прогресса при этом
 * заполнена целиком (FR-5.6, `barWidth`).
 */

export const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);

export const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

export const fmt2 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v);

const MLN = 1_000_000;
const MLRD = 1_000_000_000;

/** Тенге → млн ₸ с одним знаком (реестр, карточка, виджеты). */
export const moneyMln = (tenge: number): string => fmt1(tenge / MLN);

/**
 * Тенге → укрупнённая величина плашки: от миллиарда — млрд ₸ с двумя
 * знаками, ниже — млн ₸ с одним. Единица возвращается признаком `mlrd`,
 * подпись берётся из i18n на стороне компонента.
 */
export const moneyLarge = (tenge: number): { value: string; mlrd: boolean } =>
  Math.abs(tenge) >= MLRD
    ? { value: fmt2(tenge / MLRD), mlrd: true }
    : { value: fmt1(tenge / MLN), mlrd: false };

/** Процент с одним знаком — плашка (FR-5.4). */
export const pct1 = (v: number): string => `${fmt1(v)} %`;

/** Процент целым — полосы прогресса (FR-5.4). */
export const pctInt = (v: number): string => `${Math.round(v)}%`;

/**
 * Ширина полосы прогресса: 0…100 %. Процент выше 100 заполняет полосу
 * целиком и за её границу не выходит (FR-5.6); отрицательный — пустая.
 */
export const barWidth = (pct: number | null): string =>
  `${Math.max(0, Math.min(pct ?? 0, 100))}%`;

/**
 * ISO-дата выдачи → «MM.YYYY» (период реализации карточки, US-26 FR-4.3).
 * Нераспознанное значение возвращается как есть — подпись не должна
 * пропадать из-за незнакомого формата.
 */
export const monthYear = (iso: string | null): string | null => {
  if (iso === null) return null;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? `${m[2]}.${m[1]}` : iso;
};

/**
 * Дата актуальности загрузки → «дд.мм.гггг» (подпись «последние данные»,
 * US-27 FR-2.2). Время не показывается: пользователю важен день порции.
 */
export const loadDate = (iso: string | null): string | null => {
  if (iso === null) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('ru-RU');
};
